import { Dialog } from '@headlessui/react';
import NativeAppBridge from './components/NativeAppBridge';
import AppLoading from './components/AppLoading';
import { ReadSkeleton } from './components/ReadState';
import { loadInputPage, loadOverviewPage } from './lib/primaryPages';
import { getPublicOrigin, isNativeApp } from './lib/platform';
import useNativeBack from './hooks/useNativeBack';
import { ActionButton } from './components/FormActions';
import SheetCloseButton from './components/SheetCloseButton';
import { SheetPresence, SheetRoot } from './components/SheetMotion';
// © 2025 COPYright LAHO – https://www.linkedin.com/in/laurens-holtkamp-7a686b2b/
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import BottomNavBar from './components/BottomNavBar';
import HomescreenInstallNudge from './components/HomescreenInstallNudge';
import { useUser } from './context/AuthContext';
import { useGroup } from './context/GroupContext';
import { Beer, CalendarDays, ChevronDown, Check, MapPin, Plus, Users } from 'lucide-react';
import ToastProvider from './components/ToastProvider';
import useMatches, { MatchesProvider } from './hooks/useMatches';
import AutomatedNotificationManager from './components/AutomatedNotificationManager';
import AppOnboarding from './components/AppOnboarding';
import useAnalyticsTracking from './hooks/useAnalyticsTracking';
import { formatMatchLabel, getRecentSelectableMatches } from './utils/matches';
import useSelectedMatch, { selectMatch } from './hooks/useSelectedMatch';
import { createGathering } from './services/gatheringService';
import { supabase } from './lib/supabase';
import { getInviteGroupIdFromSearch } from './utils/inviteParams';

const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const Confirmed = lazy(() => import('./components/Confirmed'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Onboarding = lazy(() => import('./components/Onboarding'));
const MarketingPage = lazy(() => import('./components/MarketingPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));

const AdminPanel = lazy(() => import('./components/AdminPanel'));
const Profile = lazy(() => import('./components/Profile'));
const TransactionInput = lazy(loadInputPage);
const TransactionSummary = lazy(loadOverviewPage);
const TransactionLeaderboard = lazy(() => import('./components/TransactionLeaderboard'));
const TransactionHistory = lazy(() => import('./components/TransactionHistory'));
const OpenSettlements = lazy(() => import('./components/OpenSettlements'));
const PaymentRequest = lazy(() => import('./components/PaymentRequest'));
const GroupStats = lazy(() => import('./components/GroupStats'));
const SuperAdminAnalytics = lazy(() => import('./components/SuperAdminAnalytics'));

const RouteFallback = () => (
  <p className="text-center mt-8 text-gray-600">
    Pagina wordt geladen...
  </p>
);

/* ---------- Helpers ---------- */
const formatMatchChip = (m) => {
  return formatMatchLabel(m, { day: '2-digit', month: '2-digit' });
};

/* ---------- Gathering selector bottom sheet ---------- */
const MatchSelectSheet = ({ open, onClose }) => {
  useNativeBack(open, onClose);
  const { matches = [] } = useMatches();
  const { group } = useGroup();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  const visibleMatches = getRecentSelectableMatches(matches);
  const selectedMatchId = useSelectedMatch(matches);
  const currentMatchId = visibleMatches[0]?.id;

  useEffect(() => {
    if (!open || !group?.id) return;
    let active = true;
    supabase
      .from('locations')
      .select('id, name')
      .eq('group_id', group.id)
      .order('name')
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setLocations([]);
          setCreateError('Locaties konden niet worden geladen.');
          return;
        }
        setLocations(data || []);
        setLocationId((current) => current || data?.[0]?.id || '');
      });
    return () => { active = false; };
  }, [open, group?.id]);

  // body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const onSelect = (matchId) => {
    selectMatch(matchId);
    onClose?.();
  };

  const handleCreateGathering = async (event) => {
    event.preventDefault();
    const selectedLocation = locations.find((location) => location.id === locationId);
    if (!group?.id || !title.trim() || !date || !selectedLocation) {
      setCreateError('Vul een naam, datum en locatie in.');
      return;
    }
    setSaving(true);
    setCreateError('');
    const startsAt = new Date(`${date}T${time || '00:00'}`);
    try {
      const gathering = await createGathering({
        groupId: group.id,
        title: title.trim(),
        locationId: selectedLocation.id,
        locationName: selectedLocation.name,
        label: label.trim() || null,
        startsAt: startsAt.toISOString(),
      });
      window.dispatchEvent(new CustomEvent('bb:gatherings-changed'));
      onSelect(gathering.id);
    } catch (error) {
      console.error('Bijeenkomst toevoegen mislukt', error);
      setCreateError('Bijeenkomst toevoegen mislukt. Probeer het opnieuw.');
    } finally {
      setSaving(false);
    }
  };


  return (
    <SheetPresence>{open && <SheetRoot as={Dialog} open onClose={() => !saving && onClose()} className="fixed inset-0 z-[1000]">
      <div
        className="absolute inset-0 sheet-backdrop bg-black/60"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />
      <Dialog.Panel
        className="sheet-panel absolute left-0 right-0 bottom-0 rounded-t-2xl bg-white dark:bg-zinc-900
                   shadow-2xl border-t border-zinc-200 dark:border-zinc-800
                   p-4 max-h:[70dvh] max-h-[70dvh] overflow-y-auto"
      >
        <div className="flex justify-center py-1">
          <div className="h-1.5 w-12 rounded-full bg-zinc-300 dark:bg-zinc-700" />
        </div>

        <div className="mb-2 mt-1 flex items-center justify-between px-1">
          <Dialog.Title className="min-w-0 flex-1 text-base font-bold text-foreground">Bijeenkomst kiezen</Dialog.Title>
          <button type="button" onClick={() => setCreating((value) => !value)} className="inline-flex min-h-11 items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white">
            <Plus size={16} /> Nieuw
          </button>
          <SheetCloseButton disabled={saving} onClick={onClose} />
        </div>

        {creating && (
          <form onSubmit={handleCreateGathering} className="mb-3 grid gap-2 rounded-xl bg-zinc-100 p-3 dark:bg-zinc-800 sm:grid-cols-2">
            <input aria-label="Naam bijeenkomst" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Naam bijeenkomst *" className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900" />
            <input aria-label="Label (optioneel)" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optioneel)" className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900" />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Datum" className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900" />
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Tijd (optioneel)" className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900" />
            <select aria-label="Locatie" value={locationId} onChange={(event) => setLocationId(event.target.value)} className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900 sm:col-span-2">
              <option value="">Selecteer een bestaande locatie *</option>
              {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </select>
            {locations.length === 0 && <p className="text-xs text-amber-700 dark:text-amber-300 sm:col-span-2">Maak eerst onder Groep beheren een locatie aan.</p>}
            {createError && <p className="text-sm text-red-600 sm:col-span-2">{createError}</p>}
            <ActionButton type="submit" disabled={saving} className="sm:col-span-2">{saving ? 'Toevoegen…' : 'Bijeenkomst toevoegen'}</ActionButton>
          </form>
        )}

        <button
          onClick={() => onSelect(null)}
          className="min-h-11 w-full flex items-center justify-between px-3 py-2 rounded-lg
                     bg-zinc-100 hover:bg-zinc-200 text-zinc-900
                     dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-white mb-2"
        >
          <span>Automatisch (huidige bijeenkomst)</span>
          {!window.bbSelectedMatchId && (
            <Check size={16} className="opacity-80" />
          )}
        </button>

        <div className="space-y-1">
          {visibleMatches.length === 0 && (
            <p className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
              Geen bijeenkomst beschikbaar voor deze groep.
            </p>
          )}
          {visibleMatches.map((m) => {
            const active = selectedMatchId === m.id;
            return (
              <button
                key={m.id}
                onClick={() => onSelect(m.id)}
                className={`min-h-11 w-full text-left px-3 py-2 rounded-lg flex items-center justify-between
                  ${
                    active
                      ? 'bg-primary/15 text-primary'
                      : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-900 dark:bg-zinc-900/40 dark:hover:bg-zinc-900/60 dark:text-white'
                  }`}
              >
                <span className="min-w-0 pr-2">
                  <span className="block text-[10px] font-bold uppercase tracking-wide">{m.id === currentMatchId ? 'Huidige bijeenkomst' : 'Eerdere bijeenkomst'}</span>
                  <span className="block truncate font-semibold">{formatMatchChip(m)}</span>
                  <span className="mt-0.5 flex items-center gap-1 text-xs">
                    <MapPin size={12} /> {m.locationName || (m.location === 'home' ? 'Thuislocatie' : 'Locatie niet ingesteld')}
                  </span>
                </span>
                {active && <Check size={16} className="shrink-0" />}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-zinc-500 dark:text-zinc-400 px-1 mt-3">
          Je ziet de huidige bijeenkomst en maximaal twee eerdere bijeenkomsten.
          Alle eerdere transacties vind je in Geschiedenis.
        </p><ActionButton className="mt-4" variant="secondary" disabled={saving} onClick={onClose}>{creating ? 'Annuleren' : 'Sluiten'}</ActionButton>
      </Dialog.Panel>
    </SheetRoot>}</SheetPresence>
  );
};

/* ---------- Klikbare, zelf-updatende chip ---------- */
const MatchChip = ({ show = false, onClick, open = false }) => {
  const { matches = [] } = useMatches();
  const selectedId = useSelectedMatch(matches);
  const chosen = matches.find((match) => match.id === selectedId);
  const label = chosen ? formatMatchChip(chosen) : 'Bijeenkomst kiezen';

  if (!show) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 rounded-r-2xl px-3 text-left hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:hover:bg-white/[0.04]"
      title="Bijeenkomst wijzigen"
      aria-label={`Bijeenkomst wijzigen: ${label}`}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <CalendarDays size={15} strokeWidth={2.4} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] font-bold uppercase leading-none tracking-[0.12em] text-zinc-600 dark:text-zinc-400">Bijeenkomst</span>
        <span className="mt-0.5 block truncate text-sm font-bold leading-tight text-zinc-800 dark:text-zinc-100">{label}</span>
      </span>
      <ChevronDown size={16} className="shrink-0 text-zinc-400" />
    </button>
  );
};

const GroupChip = () => {
  const { group, groups = [], selectGroup } = useGroup();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useNativeBack(sheetOpen, () => setSheetOpen(false));

  const chooseGroup = async (groupId) => {
    setSheetOpen(false);
    if (groupId !== group?.id) await selectGroup(groupId);
  };

  return (
    <>
      <div className="relative min-w-0 flex-1 rounded-l-2xl">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex min-h-11 w-full min-w-0 items-center gap-2.5 rounded-l-2xl px-3 text-left hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:hover:bg-white/[0.04]"
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          aria-label={`Groep kiezen: ${group?.name || 'Geen groep'}`}
        >
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Users size={15} strokeWidth={2.4} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-bold uppercase leading-none tracking-[0.12em] text-zinc-600 dark:text-zinc-400">Groep</span>
            <span className="mt-0.5 block truncate text-sm font-bold leading-tight text-zinc-800 dark:text-zinc-100">{group?.name}</span>
          </span>
          <ChevronDown size={16} className="shrink-0 text-zinc-400" />
        </button>
      </div>
      <SheetPresence>{sheetOpen && (
        <SheetRoot as={Dialog} open onClose={() => setSheetOpen(false)} className="fixed inset-0 z-[1000]">
          <div
            aria-hidden="true"
            className="sheet-backdrop absolute inset-0 h-full w-full rounded-none bg-black/60 backdrop-blur-[2px]"
            onClick={() => setSheetOpen(false)}
          />
          <Dialog.Panel
            className="sheet-panel absolute inset-x-0 bottom-0 max-h-[70dvh] overflow-y-auto rounded-t-2xl border-t border-zinc-200 bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl dark:border-zinc-800 dark:bg-zinc-900"
          >
            <div className="flex justify-center py-1">
              <div className="h-1.5 w-12 rounded-full bg-zinc-300 dark:bg-zinc-700" />
            </div>
            <div className="mb-3 mt-1 flex items-center justify-between px-1">
              <div>
                <Dialog.Title className="text-base font-bold text-foreground">Groep kiezen</Dialog.Title>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">Kies met welke groep je de app gebruikt.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSheetOpen(false);
                  setOnboardingOpen(true);
                }}
                className="inline-flex min-h-11 items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white"
              >
                <Plus size={16} /> Nieuw
              </button>
              <SheetCloseButton label="Groepsselectie sluiten" onClick={() => setSheetOpen(false)} />
            </div>
            <div className="space-y-1" role="group" aria-label="Beschikbare groepen">
              {groups.map((item) => {
                const active = item.id === group?.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => chooseGroup(item.id)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left ${active ? 'bg-primary/15 text-primary' : 'bg-zinc-50 text-zinc-900 hover:bg-zinc-100 dark:bg-zinc-800/70 dark:text-white dark:hover:bg-zinc-800'}`}
                  >
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${active ? 'bg-primary text-white' : 'bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-300'}`}>
                      <Users size={17} />
                    </span>
                    <span className="min-w-0 flex-1 truncate font-semibold">{item.name}</span>
                    {active && <Check size={18} className="shrink-0" />}
                  </button>
                );
              })}
            </div>
            <ActionButton className="mt-4" variant="secondary" onClick={() => setSheetOpen(false)}>Sluiten</ActionButton>
          </Dialog.Panel>
        </SheetRoot>
      )}</SheetPresence>
      {onboardingOpen && typeof document !== 'undefined' && createPortal(
        <Onboarding embedded onClose={() => setOnboardingOpen(false)} />,
        document.body
      )}
    </>
  );
};

/* ---------- Pagina layout met grotere header-titel ---------- */
const PageLayout = ({ children, pageTitle = '', showMatchChip = false }) => {
  const scrollRef = useRef(null);
  const [animateLogo, setAnimateLogo] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setAnimateLogo(false), 650);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isNativeApp()) scrollRef.current?.scrollTo({ top: 0 });
    setAnimateLogo(true);
    const t = setTimeout(() => setAnimateLogo(false), 450);
    return () => clearTimeout(t);
  }, [location.pathname]);

  useEffect(() => {
    const onToast = () => {
      setAnimateLogo(true);
      const t = setTimeout(() => setAnimateLogo(false), 450);
      return () => clearTimeout(t);
    };
    window.addEventListener('bb:toast', onToast);
    return () => window.removeEventListener('bb:toast', onToast);
  }, []);

  useEffect(() => {
    const open = () => setSheetOpen(true);
    window.addEventListener('bb:open-match-picker', open);
    return () => window.removeEventListener('bb:open-match-picker', open);
  }, []);

  useLayoutEffect(() => {
    const header = document.querySelector('.bb-app-layout > header');
    const navigation = document.querySelector('[data-native-tabs]');
    const measure = () => {
      if (header) document.documentElement.style.setProperty('--bb-header-height', `${header.getBoundingClientRect().height}px`);
      if (navigation) document.documentElement.style.setProperty('--bb-nav-height', `${navigation.getBoundingClientRect().height}px`);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    if (header) observer.observe(header);
    if (navigation) observer.observe(navigation);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bb-app-layout flex flex-col min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 relative">
      <a href="#app-content" className="bb-skip-link" onClick={() => scrollRef.current?.focus()}>Direct naar inhoud</a>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-zinc-200/70 bg-white/85 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] shadow-[0_8px_30px_-24px_rgba(24,24,27,0.7)] backdrop-blur-xl dark:border-white/10 dark:bg-zinc-950/80">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary to-primary-dark text-white shadow-lg shadow-primary/20">
              <Beer className={`h-5 w-5 ${animateLogo ? 'animate-bounce' : ''}`} strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1">
              <span className="block text-[10px] font-black uppercase leading-none tracking-[0.2em] text-primary">Cuplio</span>
              <h1 className="mt-1 break-words text-xl font-black leading-tight tracking-tight text-zinc-900 dark:text-white">{pageTitle}</h1>
            </div>
          </div>
          <div className="mt-3 flex min-w-0 divide-x divide-zinc-200/80 overflow-visible rounded-2xl border border-zinc-200/80 bg-white/80 shadow-sm dark:divide-white/10 dark:border-white/10 dark:bg-white/[0.05]" aria-label="Groep en bijeenkomst">
            <GroupChip />
            <MatchChip open={sheetOpen} show={showMatchChip} onClick={() => setSheetOpen(true)} />
          </div>
        </div>
      </header>

      <main
        ref={scrollRef}
        id="app-content"
        tabIndex={-1}
        aria-label={pageTitle}
        className="bb-page flex-1 overflow-y-auto px-4 pt-32"
      >
        <HomescreenInstallNudge />
        <div key={location.pathname} className="bb-screen-enter"><Suspense fallback={<ReadSkeleton label="Pagina wordt geladen…" />}>{children}</Suspense></div>
      </main>

      <BottomNavBar />
      <MatchSelectSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
};

/* ---------- Router ---------- */
const StaticPage = ({ title, children }) => (
  <div className="min-h-screen bg-zinc-950 px-4 py-12 text-white">
    <div className="mx-auto max-w-3xl rounded-3xl bg-white p-8 text-zinc-950">
      <h1 className="text-3xl font-black">{title}</h1>
      <div className="mt-4 space-y-4 text-zinc-700">{children}</div>
      <a className="mt-8 inline-flex rounded-full bg-emerald-400 px-5 py-2 font-bold text-zinc-950" href="/">Terug naar Cuplio</a>
    </div>
  </div>
);

const NativeContact = () => (
  <StaticPage title="Contact">
    <p>Stuur ons je vraag of feedback via het contactformulier.</p>
    <a href={`${getPublicOrigin()}/contact`} className="inline-flex min-h-12 items-center rounded-xl bg-primary px-4 font-bold text-white">Open contactformulier</a>
  </StaticPage>
);

const AppRouter = () => {
  const { user, authLoaded } = useUser();
  const { group, loadingGroup } = useGroup();
  const location = useLocation();
  useAnalyticsTracking(user, group);

  if (!authLoaded) {
    return (
      <AppLoading />
    );
  }

  // Betaallinks moeten ook voor een uitgelogde of verkeerde gebruiker hun
  // context kunnen tonen; alleen bevestigen blijft accountgebonden.
  if (['/p', '/pay'].includes(location.pathname) || location.pathname.startsWith('/p/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/p/:code" element={<PaymentRequest />} />
          <Route path="/p" element={<PaymentRequest />} />
          <Route path="/pay" element={<PaymentRequest />} />
        </Routes>
      </Suspense>
    );
  }

  // Een resetlink maakt tijdelijk een ingelogde recovery-sessie aan. Handel de
  // resetroute daarom af voordat de groep/onboarding-routering wordt bepaald.
  if (location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  // Contact blijft bereikbaar ongeacht login- of onboardingstatus.
  if (location.pathname === '/contact') {
    return (
      <Routes>
        <Route path="/contact" element={isNativeApp() ? <NativeContact /> : <ContactPage />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={isNativeApp() ? <Navigate to="/login" replace /> : <MarketingPage />} />
        <Route path="/privacy" element={<StaticPage title="Privacy"><p>Cuplio verwerkt alleen gegevens die nodig zijn om in te loggen, matchdays te gebruiken en de dienst te verbeteren.</p></StaticPage>} />
        <Route path="/terms" element={<StaticPage title="Voorwaarden"><p>Gebruik Cuplio eerlijk met je groep. Deze voorwaarden worden uitgebreid voor publieke lancering.</p></StaticPage>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/confirmed" element={<Confirmed />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  // Preserve invitations even when signing in changes the active route immediately.
  const inviteGroupId = getInviteGroupIdFromSearch(location.search);
  if (location.pathname === '/confirmed') return <Confirmed />;
  if (location.pathname === '/onboarding') return <Onboarding />;
  if (user && inviteGroupId && ['/login', '/register'].includes(location.pathname)) {
    return <Navigate to={`/onboarding?group=${encodeURIComponent(inviteGroupId)}`} replace />;
  }

  if (loadingGroup) {
    return (
      <AppLoading label="Je groep wordt geladen…" />
    );
  }

  if (!group) {
    return (
      <Routes>
        <Route path="/" element={isNativeApp() ? <Navigate to="/onboarding" replace /> : <MarketingPage />} />
        <Route path="/privacy" element={<StaticPage title="Privacy"><p>Cuplio verwerkt alleen gegevens die nodig zijn om in te loggen, matchdays te gebruiken en de dienst te verbeteren.</p></StaticPage>} />
        <Route path="/terms" element={<StaticPage title="Voorwaarden"><p>Gebruik Cuplio eerlijk met je groep. Deze voorwaarden worden uitgebreid voor publieke lancering.</p></StaticPage>} />
        <Route
          path="/super-admin"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SuperAdminAnalytics />
            </Suspense>
          }
        />
        <Route
          path="/super-admin/analytics"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SuperAdminAnalytics />
            </Suspense>
          }
        />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="*" element={<Navigate to={`/onboarding${location.search || ''}`} />} />
      </Routes>
    );
  }

  return (
    <MatchesProvider key={`${user.id}:${group.id}`}>
    <Suspense fallback={<RouteFallback />}>
      <AutomatedNotificationManager />
      <AppOnboarding />
      <Routes>
      <Route
        path="/onboarding"
        element={<Onboarding />}
      />
      <Route
        path="/app"
        element={
          <PageLayout pageTitle="Overzicht" showMatchChip>
            <TransactionSummary />
          </PageLayout>
        }
      />
      <Route
        path="/input"
        element={
          <PageLayout pageTitle="Consumpties toevoegen" showMatchChip>
            <TransactionInput />
          </PageLayout>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <PageLayout pageTitle="Klassement" showMatchChip>
            <TransactionLeaderboard />
          </PageLayout>
        }
      />
      <Route
        path="/log"
        element={
          <PageLayout pageTitle="Geschiedenis" showMatchChip>
            <TransactionHistory />
          </PageLayout>
        }
      />
      <Route
        path="/settlements"
        element={
          <PageLayout pageTitle="Afrekenen">
            <OpenSettlements />
          </PageLayout>
        }
      />
      <Route
        path="/admin"
        element={
          <PageLayout pageTitle="Groep beheren">
            <AdminPanel />
          </PageLayout>
        }
      />
      <Route
        path="/profile"
        element={
          <PageLayout pageTitle="Profiel">
            <Profile />
          </PageLayout>
        }
      />
      <Route
        path="/stats"
        element={
          <PageLayout pageTitle="Groepsstatistieken" showMatchChip>
            <GroupStats />
          </PageLayout>
        }
      />
      <Route path="/super-admin" element={<SuperAdminAnalytics />} />
      <Route path="/super-admin/analytics" element={<SuperAdminAnalytics />} />
      <Route path="/confirmed" element={<Confirmed />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Navigate to="/input" replace />} />
      <Route path="/privacy" element={<StaticPage title="Privacy"><p>Cuplio verwerkt alleen gegevens die nodig zijn om in te loggen, matchdays te gebruiken en de dienst te verbeteren.</p></StaticPage>} />
      <Route path="/terms" element={<StaticPage title="Voorwaarden"><p>Gebruik Cuplio eerlijk met je groep. Deze voorwaarden worden uitgebreid voor publieke lancering.</p></StaticPage>} />
      <Route path="*" element={<Navigate to="/input" replace />} />
      </Routes>
    </Suspense>
    </MatchesProvider>
  );
};

const WrappedRouter = () => (
  <ToastProvider>
    <Router>
      <NativeAppBridge />
      <Suspense fallback={<AppLoading />}><AppRouter /></Suspense>
    </Router>
  </ToastProvider>
);

export default WrappedRouter;
